import React from "react";
import { useTranslation } from "react-i18next";
import { CreateCommunityForm } from "../../../../types/forms";
import { CommunityType } from "../../../../types/community";
import { BusinessType } from "../../../../types/business";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import FeaturesForm from "../../../components/misc/FeaturesForm";
import { getMinimumSubscriptionForFeatures } from "../../../../utils/FeatureUtils";
import AreYouSureModal from "../../../components/modals/AreYouSureModal";

type SubCommunitySetupViewProps = {
  setStep: (step: 1 | -1) => void;
  community: CreateCommunityForm;
  setCommunity: (community: CreateCommunityForm) => void;
  businessType?: BusinessType;
}

const getDefaultFeatures = (communityType: CommunityType) => {
  switch (communityType) {
    case CommunityType.SPORTTEAM:
      return {
        EASY_SHARING: true,
        MEMBER_ADMINISTRATION: true,
        EVENTS: true,
        PARTICIPATION_INSIGHTS: true,
        TASKS: true,
        STATS_AND_SCORES: true,
        COMMUNICATION: true,
        CHATS: true,
        COST_SPLITTING: true,
      };
    case CommunityType.STUDENTGROUP:
      return {
        EASY_SHARING: true,
        MEMBER_ADMINISTRATION: true,
        EVENTS: true,
        PARTICIPATION_INSIGHTS: true,
        STATS_AND_SCORES: true,
        COMMUNICATION: true,
        CHATS: true,
        COST_SPLITTING: true,
        PHOTO_ALBUMS: true,
      };
    default:
      return {
        EASY_SHARING: true,
        EVENTS: true,
        CHATS: true,
        COST_SPLITTING: true,
      };
  }
};

const deriveCommunityTypeFromBusinessType = (businessType?: BusinessType): CommunityType => {
  switch (businessType) {
    case BusinessType.SPORTCLUB:
      return CommunityType.SPORTTEAM;
    case BusinessType.STUDENTASSOCIATION:
      return CommunityType.STUDENTGROUP;
    default:
      return CommunityType.OTHER_COMMUNITY;
  }
};

export default function SubCommunitySetupView({ 
  community, 
  setCommunity, 
  setStep, 
  businessType 
}: SubCommunitySetupViewProps) {
  const { t } = useTranslation();
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [deferredStepChange, setDeferredStepChange] = React.useState<null | 1 | -1>(null);

  // Derive community type from business type and set default features
  React.useEffect(() => {
    const derivedType = deriveCommunityTypeFromBusinessType(businessType);
    if (!community.type || community.type !== derivedType) {
      const newFeatures = getDefaultFeatures(derivedType);
      const requiredSubscription = getMinimumSubscriptionForFeatures(newFeatures);
      
      setCommunity({
        ...community,
        type: derivedType,
        features: newFeatures,
        connectedMembers: newFeatures.EASY_SHARING,
        subscriptionType: requiredSubscription,
      });
    }
  }, [businessType]);

  const wrappedSetStep = (diff: 1 | -1) => {
    const features = community.features || {};
    const anySelected = Object.values(features).some(Boolean);

    if (!anySelected && diff === 1) {
      setDeferredStepChange(diff);
      setWarningOpen(true);
    } else {
      setStep(diff);
    }
  };

  return (
    <>
      <StepFlowStep
        disabled={!community.name || community.features === undefined}
        setStep={wrappedSetStep}
        title={t('page.onboarding.subcommunity.setup.title')}
        subtitle={t('page.onboarding.subcommunity.setup.subtitle')}
        className="max-w-4xl">
        <div className="flex flex-col gap-6 pr-2">
          {/* Name Input */}
          <div className="relative flex w-1/2 flex-shrink-0 flex-col gap-1">
            <div className="hidden items-start justify-between gap-1 sm:flex">
              <p className="font-semibold">{t(`page.onboarding.community.info.name`)}</p>
            </div>
            <div className="relative w-full">
              <div className="h-10 sm:hidden"> </div>
              <input
                type="text"
                className="w-full"
                placeholder={`${t('general.name')}...`}
                value={community.name || ''}
                onChange={(e) => setCommunity({ ...community, name: e.target.value })}
              />
              <p className="absolute right-4 top-2 text-sm sm:hidden">{t('general.mandatory')}</p>
            </div>
          </div>

          {/* Features Form */}
          <div className="flex flex-col gap-2">
            <FeaturesForm
              features={community.features || {}}
              onFeaturesChange={(features) => {
                const requiredSubscription = getMinimumSubscriptionForFeatures(features);
                setCommunity({
                  ...community,
                  connectedMembers: features.EASY_SHARING,
                  features,
                  subscriptionType: requiredSubscription,
                });
              }}
            />
          </div>
        </div>
      </StepFlowStep>

      <AreYouSureModal
        open={warningOpen}
        setOpen={setWarningOpen}
        title={t('page.onboarding.communityFeaturesScreen.modal.title')}
        description={t('page.onboarding.communityFeaturesScreen.modal.description')}
        confirmLabel={t('page.onboarding.communityFeaturesScreen.modal.confirm')}
        cancelLabel={t('page.onboarding.communityFeaturesScreen.modal.cancel')}
        onConfirm={() => {
          setWarningOpen(false);
          if (deferredStepChange) {
            setStep(deferredStepChange);
            setDeferredStepChange(null);
          }
        }}
        onCancel={() => {
          setWarningOpen(false);
          setDeferredStepChange(null);
        }}
      />
    </>
  );
}
